import React, { useState } from 'react';
import { observer } from 'mobx-react-lite';
import { GeneratedPassword, PasswordStrength } from '../../types/password.types';
import './password-display-card.scss';

interface PasswordDisplayCardProps {
  password: GeneratedPassword;
  showPassword: boolean;
  onCopy: (password: string) => Promise<boolean>;
  onRegenerate: (passwordId: string) => void;
  onRemove: (passwordId: string) => void;
}

export const PasswordDisplayCard: React.FC<PasswordDisplayCardProps> = observer(({
  password,
  showPassword,
  onCopy,
  onRegenerate,
  onRemove,
}) => {
  const [copySuccess, setCopySuccess] = useState(false);

  const handleCopy = async () => {
    const success = await onCopy(password.value);
    if (success) {
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2000);
    }
  };

  const getStrengthColor = (strength: PasswordStrength): string => {
    switch (strength) {
      case PasswordStrength.VERY_WEAK:
        return '#ff4444';
      case PasswordStrength.WEAK:
        return '#ff8800';
      case PasswordStrength.FAIR:
        return '#ffbb00';
      case PasswordStrength.GOOD:
        return '#88cc00';
      case PasswordStrength.STRONG:
        return '#00cc44';
      default:
        return '#666';
    }
  };

  const getStrengthLabel = (strength: PasswordStrength): string => {
    switch (strength) {
      case PasswordStrength.VERY_WEAK:
        return 'Very Weak';
      case PasswordStrength.WEAK:
        return 'Weak';
      case PasswordStrength.FAIR:
        return 'Fair';
      case PasswordStrength.GOOD:
        return 'Good';
      case PasswordStrength.STRONG:
        return 'Strong';
      default:
        return 'Unknown';
    }
  };

  const displayValue = showPassword 
    ? password.value 
    : '•'.repeat(password.value.length);

  return (
    <div className="password-display-card">
      <div className="password-display-card__header">
        <div 
          className="password-display-card__strength"
          style={{ color: getStrengthColor(password.strength) }}
        >
          <span className="password-display-card__strength-dot"></span>
          {getStrengthLabel(password.strength)}
        </div>
        <div className="password-display-card__actions">
          <button
            onClick={() => onRegenerate(password.id)}
            className="password-display-card__action-btn"
            title="Regenerate"
          >
            🔄
          </button>
          <button
            onClick={() => onRemove(password.id)}
            className="password-display-card__action-btn"
            title="Remove"
          >
            🗑️
          </button>
        </div>
      </div>

      <div className="password-display-card__content">
        <div className="password-display-card__password">
          <span className="password-display-card__password-text">
            {displayValue}
          </span>
        </div>
        
        <div className="password-display-card__info">
          <span className="password-display-card__length">
            {password.value.length} characters
          </span>
        </div>
      </div>

      <div className="password-display-card__footer">
        <button
          onClick={handleCopy}
          className={`password-display-card__copy-btn ${copySuccess ? 'password-display-card__copy-btn--success' : ''}`}
          disabled={copySuccess}
        >
          {copySuccess ? '✓ Copied!' : '📋 Copy'}
        </button>
      </div>
    </div>
  );
});
